import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { Container, Section } from '../styles/GlobalStyles';
import NetworkBackground from '../components/Particles/NetworkBackground';
import { FiMail, FiUser, FiMessageSquare, FiPhone, FiBriefcase, FiSend, FiCheckCircle } from 'react-icons/fi';

const ContactContainer = styled(Section)`
  min-height: calc(100vh - 80px);
  background: ${theme.colors.neutral.white};
  padding-top: calc(80px + ${theme.spacing['3xl']});
  padding-bottom: ${theme.spacing['3xl']};
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;

  @media (max-width: ${theme.breakpoints.md}) {
    padding-top: calc(70px + ${theme.spacing.xl});
    padding-bottom: ${theme.spacing.xl};
    min-height: auto;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding-top: calc(70px + ${theme.spacing.lg});
    padding-bottom: ${theme.spacing.lg};
    min-height: auto;
  }
`;

const ContactContent = styled.div`
  max-width: 600px;
  margin: 0 auto;
  width: 100%;
  position: relative;
  z-index: 2;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.sm}) {
    margin-bottom: ${theme.spacing.lg};
  }
`;

const Title = styled.h1`
  font-size: ${theme.typography.fontSize['3xl']};
  margin-bottom: ${theme.spacing.sm};
  
  .highlight {
    color: ${theme.colors.primary.synapse};
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: 2px;
      left: -2px;
      right: -2px;
      height: 6px;
      background: ${theme.colors.primary.accent};
      opacity: 0.8;
      border-radius: 8px;
      transform: rotate(-0.5deg) scaleY(0.6);
      z-index: -1;
    }
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: ${theme.typography.fontSize.xl};
    margin-bottom: ${theme.spacing.xs};
  }
`;


const FormSection = styled.div`
  background: ${theme.colors.neutral.white};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  border: 1px solid ${theme.colors.neutral.gray200};
  transition: all ${theme.transitions.normal};

  &:focus-within {
    border-color: ${theme.colors.primary.synapse};
    box-shadow: 0 0 0 2px ${theme.colors.primary.synapse}15;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.md};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};

  @media (max-width: ${theme.breakpoints.sm}) {
    gap: ${theme.spacing.sm};
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.neutral.gray700};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};

  svg {
    width: 14px;
    height: 14px;
    color: ${theme.colors.primary.synapse};
  }
`;

const Input = styled.input`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.neutral.gray300};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-family: ${theme.typography.fontFamily.primary};
  color: ${theme.colors.neutral.gray800};
  transition: all ${theme.transitions.normal};
  background: ${theme.colors.neutral.white};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.synapse};
    background: ${theme.colors.neutral.light};
    box-shadow: 0 0 0 2px ${theme.colors.primary.synapse}10;
  }

  &::placeholder {
    color: ${theme.colors.neutral.gray400};
  }

  &:disabled {
    background: ${theme.colors.neutral.gray100};
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.neutral.gray300};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-family: ${theme.typography.fontFamily.primary};
  color: ${theme.colors.neutral.gray800};
  transition: all ${theme.transitions.normal};
  background: ${theme.colors.neutral.white};
  min-height: 100px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.synapse};
    background: ${theme.colors.neutral.light};
    box-shadow: 0 0 0 2px ${theme.colors.primary.synapse}10;
  }

  &::placeholder {
    color: ${theme.colors.neutral.gray400};
  }

  &:disabled {
    background: ${theme.colors.neutral.gray100};
    cursor: not-allowed;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${theme.spacing.md};

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const SubmitButton = styled.button<{ $isSuccess?: boolean }>`
  background: ${props => props.$isSuccess ? theme.colors.success : theme.colors.primary.synapse};
  color: ${theme.colors.neutral.white};
  padding: ${theme.spacing.sm} ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.md};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.base};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.xs};
  transition: all ${theme.transitions.normal};
  margin-top: ${theme.spacing.sm};
  cursor: ${props => props.$isSuccess ? 'default' : 'pointer'};

  &:hover:not(:disabled) {
    background: ${props => props.$isSuccess ? theme.colors.success : theme.colors.primary.accent};
    transform: translateY(-2px);
    box-shadow: 0 3px 8px ${theme.colors.primary.synapse}30;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    background: ${theme.colors.neutral.gray400};
    cursor: not-allowed;
    transform: none;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ErrorMessage = styled.p`
  color: ${theme.colors.error};
  font-size: ${theme.typography.fontSize.xs};
  margin-top: ${theme.spacing.xs};
`;

const SuccessMessage = styled.div`
  background: ${theme.colors.success}10;
  border: 1px solid ${theme.colors.success};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  color: ${theme.colors.success};
  font-weight: ${theme.typography.fontWeight.medium};
  font-size: ${theme.typography.fontSize.sm};
  margin-top: ${theme.spacing.md};

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
`;

interface FormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  message: string;
}

interface FormErrors {
  [key: string]: string;
}

const ContatoPage: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'E-mail é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'E-mail inválido';
    }


    if (!formData.message.trim()) {
      newErrors.message = 'Mensagem é obrigatória';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Mensagem deve ter pelo menos 10 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Limpa o erro do campo quando o usuário começa a digitar
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Envia os dados para o endpoint PHP
      const response = await fetch('/api/enviar-contato.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setIsSuccess(true);
        
        // Limpa o formulário após 5 segundos
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            phone: '',
            company: '',
            message: '',
          });
          setIsSuccess(false);
        }, 5000);
      } else {
        throw new Error(result.message || 'Erro ao enviar mensagem');
      }
      
    } catch (error) {
      console.error('Erro ao enviar formulário:', error);
      setErrors({ 
        submit: error instanceof Error 
          ? error.message 
          : 'Erro ao enviar mensagem. Tente novamente.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContactContainer>
      <NetworkBackground accentColor={theme.colors.primary.synapse} />
      
      <Container style={{ position: 'relative', zIndex: 2 }}>
        <ContactContent>
          <Header>
            <Title>
              Entre em <span className="highlight">contato</span> conosco
            </Title>
          </Header>

          <FormSection>
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="name">
                  <FiUser /> Nome Completo *
                </Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Seu nome completo"
                  disabled={isSubmitting || isSuccess}
                />
                {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <Label htmlFor="email">
                    <FiMail /> E-mail *
                  </Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="seu@email.com"
                    disabled={isSubmitting || isSuccess}
                  />
                  {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="phone">
                    <FiPhone /> Telefone
                  </Label>
                  <Input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(11) 99999-9999"
                    disabled={isSubmitting || isSuccess}
                  />
                </FormGroup>
              </FormRow>

              <FormGroup>
                <Label htmlFor="company">
                  <FiBriefcase /> Empresa
                </Label>
                <Input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Nome da sua empresa"
                  disabled={isSubmitting || isSuccess}
                />
              </FormGroup>


              <FormGroup>
                <Label htmlFor="message">
                  <FiMessageSquare /> Mensagem *
                </Label>
                <TextArea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Conte-nos mais sobre seu projeto, desafios ou dúvidas..."
                  disabled={isSubmitting || isSuccess}
                />
                {errors.message && <ErrorMessage>{errors.message}</ErrorMessage>}
              </FormGroup>

              {errors.submit && <ErrorMessage>{errors.submit}</ErrorMessage>}

              <SubmitButton 
                type="submit" 
                disabled={isSubmitting || isSuccess}
                $isSuccess={isSuccess}
              >
                {isSubmitting ? (
                  <>Enviando...</>
                ) : isSuccess ? (
                  <>
                    <FiCheckCircle /> Mensagem Enviada!
                  </>
                ) : (
                  <>
                    <FiSend /> Enviar Mensagem
                  </>
                )}
              </SubmitButton>

              {isSuccess && (
                <SuccessMessage>
                  <FiCheckCircle />
                  <span>
                    Obrigado pelo contato! Recebemos sua mensagem e retornaremos em breve.
                  </span>
                </SuccessMessage>
              )}
            </Form>
          </FormSection>
        </ContactContent>
      </Container>
    </ContactContainer>
  );
};

export default ContatoPage;

